(() => {
  'use strict';
  let feature;
  // Model metadata is independent of purchasing: bad/missing models never disable sales.
  const safeURL = (url, suffix) => {
    try {
      if (typeof url !== 'string' || url.length > 2048 || /[\s<>"'\\]/.test(url)) return false;
      const u = new URL(url);
      return u.protocol === 'https:' && u.hostname === 'cdn.shopify.com' &&
        !u.port && !u.username && !u.password && !u.hash &&
        u.pathname.startsWith('/3d/models/') && u.pathname.endsWith(suffix);
    } catch (_) { return false; }
  };
  function modelMap(value) {
    if (!value || value.schema_version !== 1 || !value.by_sku ||
        typeof value.by_sku !== 'object' || Array.isArray(value.by_sku)) return null;
    const entries = Object.entries(value.by_sku);
    if (!entries.length || entries.length > 2048 || JSON.stringify(value).length > 1048576) return null;
    const result = Object.create(null);
    for (const [sku, entry] of entries) {
      if (!/^[A-Z0-9]+(?:-[A-Z0-9]+)+$/.test(sku) || sku.length > 100 ||
          !entry || !safeURL(entry.glb, '.glb') || !safeURL(entry.usdz, '.usdz') ||
          !/^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(entry.productId) ||
          !/^gid:\/\/shopify\/Model3d\/[1-9][0-9]*$/.test(entry.modelId)) return null;
      result[sku] = Object.freeze({ ...entry });
    }
    return Object.freeze(result);
  }
  function ready() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!feature) feature = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('timeout')), 20000);
      if (!window.Shopify?.loadFeatures) { clearTimeout(timeout); reject(new Error('loader')); return; }
      Shopify.loadFeatures([{ name: 'model-viewer', version: '1.12', onLoad(errors) {
        if (errors) { clearTimeout(timeout); reject(errors); return; }
        customElements.whenDefined('model-viewer').then(() => { clearTimeout(timeout); resolve(); });
      }}]);
    }).catch(error => { feature = null; throw error; });
    return feature;
  }
  async function initialize(config) {
    if (config.dataset.bound) return;
    config.dataset.bound = 'true';
    const root = config.closest('.pl8w');
    if (!root) return;
    let settings, models;
    try {
      settings = JSON.parse(config.textContent);
      models = modelMap(settings.models);
      // A frozen legacy manifest supports migration/rollback, never new model publishing.
      if (!models) {
        const response = await fetch(settings.catalog);
        if (!response.ok) return;
        models = modelMap(await response.json());
      }
      if (!models) return;
    } catch (_) { return; }
    let active = null;
    function close(restoreFocus = false) {
      if (!active) return;
      const previous = active;
      active = null;
      previous.viewer?.remove();
      previous.frame.classList.remove('pl8w-model-active');
      previous.button.textContent = settings.open;
      previous.button.setAttribute('aria-label', previous.name + ' — ' + settings.open);
      previous.button.setAttribute('aria-expanded', 'false');
      previous.status.textContent = '';
      if (restoreFocus) previous.button.focus();
    }
    root.querySelectorAll('[data-catalog-sku]').forEach(card => {
      const entry = models[card.dataset.catalogSku.replace(/^WS-/, '')];
      const frame = card.querySelector('.pl8w__card-image');
      const image = frame?.querySelector('img');
      if (!entry || !image || !safeURL(entry.glb, '.glb') || !safeURL(entry.usdz, '.usdz')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pl8w-model-toggle';
      button.textContent = settings.open;
      button.setAttribute('aria-label', image.alt + ' — ' + settings.open);
      button.setAttribute('aria-expanded', 'false');
      const status = document.createElement('span');
      status.className = 'pl8w-model-status';
      status.setAttribute('role', 'status');
      frame.append(button, status);
      button.addEventListener('click', async () => {
        if (active?.button === button) { close(); return; }
        close();
        const selection = { card, frame, button, status, viewer: null, name: image.alt };
        active = selection;
        button.textContent = settings.close;
        button.setAttribute('aria-label', image.alt + ' — ' + settings.close);
        button.setAttribute('aria-expanded', 'true');
        status.textContent = settings.loading;
        try {
          await ready();
          if (active !== selection || card.hidden) return;
          const viewer = document.createElement('model-viewer');
          selection.viewer = viewer;
          viewer.setAttribute('src', entry.glb);
          viewer.setAttribute('ios-src', entry.usdz);
          viewer.setAttribute('alt', image.alt);
          viewer.setAttribute('camera-controls', '');
          viewer.setAttribute('touch-action', 'pan-y');
          viewer.setAttribute('interaction-prompt', 'none');
          viewer.setAttribute('ar', '');
          viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
          viewer.setAttribute('ar-scale', 'fixed');
          viewer.setAttribute('ar-placement', 'wall');
          viewer.setAttribute('poster', image.currentSrc || image.src);
          const ar = document.createElement('button');
          ar.type = 'button';
          ar.slot = 'ar-button';
          ar.className = 'pl8w-model-ar';
          ar.textContent = settings.ar;
          viewer.append(ar);
          viewer.addEventListener('load', () => { if (active === selection) status.textContent = ''; });
          const failed = () => { if (active === selection) { close(); status.textContent = settings.error; } };
          viewer.addEventListener('error', failed);
          viewer.addEventListener('ar-status', event => { if (event.detail.status === 'failed') status.textContent = settings.arError; });
          frame.append(viewer);
          frame.classList.add('pl8w-model-active');
        } catch (_) { if (active === selection) { close(); status.textContent = settings.error; } }
      });
    });
    root.addEventListener('keydown', event => { if (event.key === 'Escape' && active) close(true); });
    new MutationObserver(() => { if (active?.card.hidden) close(); }).observe(root, { attributes: true, attributeFilter: ['hidden'], subtree: true });
  }
  const boot = () => document.querySelectorAll('[data-pl8-model-config]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
